import { Component, OnInit, ChangeDetectionStrategy, OnDestroy } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { Select, Store, Actions, ofActionCompleted } from '@ngxs/store';
import { AuthState } from '../../state/auth/auth.state';
import { Observable, Subject } from 'rxjs';
import { UserProfileModel } from '../../models/user-profile.model';
import { filter, takeUntil } from 'rxjs/operators';
import { GetUserProfile, Logout, LogoutSuccess, LogoutFailure, SignUpFailure, LoginFailure } from '../../state/auth/auth.actions';
import { LoginArt } from '../../models/login-art.enum';
import * as _ from 'lodash';
import { MatSnackBar } from '@angular/material';

@Component({
  selector: 'app-login-master',
  templateUrl: './login-master.component.html',
  styleUrls: ['./login-master.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LoginMasterComponent implements OnInit, OnDestroy {
  loginArt: LoginArt;

  @Select(AuthState.getLoggedIn)
  loggedIn$: Observable<boolean>;

  @Select(AuthState.getUserProfile)
  userProfile$: Observable<UserProfileModel>;

  private ngUnsubscribe = new Subject();

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private store: Store,
    private actions: Actions,
    private snackbar: MatSnackBar
  ) { }

  ngOnInit() {
    // Automatically log users out when they come to this page, primarily so we can 
    // ensure things like group invitations will work.
    this.actions.pipe(
      ofActionCompleted(LogoutSuccess, LogoutFailure),
      takeUntil(this.ngUnsubscribe)
    ).subscribe(() => {
      this.initLoginListeners();
    });
    this.store.dispatch(new Logout());

    this.activatedRoute.url.subscribe((url) => {
      this.loginArt = _.get(this.activatedRoute, 'snapshot.firstChild.data.loginArt') || LoginArt.login;
    });

    this.actions.pipe(
      ofActionCompleted(SignUpFailure, LoginFailure),
      takeUntil(this.ngUnsubscribe)
    ).subscribe((action) => {
      // This is why it's nested:
      // <stateFailure>.<calledAction>.<HttpErrorResponse>.<Error of HttpErrorResponse>.<Custom Error Object>.<Message>
      const message = _.get(action, 'action.error.error.error.message') || 'There was an error logging in.';
      this.handleFailure(message);
    });
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  initLoginListeners() {
    this.loggedIn$.pipe(
      filter(loggedIn => loggedIn),
      takeUntil(this.ngUnsubscribe)
    ).subscribe((loggedIn) => {
      this.store.dispatch(new GetUserProfile());
    });

    this.userProfile$.pipe(
      filter(profile => profile != null),
      takeUntil(this.ngUnsubscribe)
    ).subscribe((profile) => {
      this.redirect();
    });
  }

  redirect() {
    const returnUrl = this.activatedRoute.snapshot.queryParamMap.get('returnUrl');

    if (returnUrl) {
      this.router.navigateByUrl(returnUrl);
    } else {
      this.router.navigate(['/dashboard']);
    }
  }

  handleFailure(message: string) {
    this.snackbar.open(message, '', { duration: 10000 });
  }
}
